import React, { useState, useEffect, useCallback } from 'react';
import Layout from '../components/Layout';
import { Inbox, ShieldAlert, CheckCircle2, Clock, Mail, Eye, X } from 'lucide-react';
import axios from 'axios';
import { getAuthHeaders } from '../utils/auth';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const STATUS_META = {
  quote_applied: { label: 'QUOTE APPLIED', cls: 'text-green-400 border-green-500/40 bg-green-500/10', icon: CheckCircle2 },
  matched_needs_owner: { label: 'MATCHED · NEEDS YOU', cls: 'text-blue-400 border-blue-500/40 bg-blue-500/10', icon: Eye },
  held_for_review: { label: 'HELD FOR REVIEW', cls: 'text-yellow-400 border-yellow-500/40 bg-yellow-500/10', icon: Clock },
  quarantined: { label: 'QUARANTINED', cls: 'text-red-400 border-red-500/40 bg-red-500/10', icon: ShieldAlert },
  received: { label: 'PROCESSING', cls: 'text-zinc-400 border-white/20 bg-white/5', icon: Mail },
  reviewed: { label: 'REVIEWED', cls: 'text-zinc-400 border-white/20 bg-white/5', icon: CheckCircle2 },
  dismissed: { label: 'DISMISSED', cls: 'text-zinc-500 border-white/10 bg-white/5', icon: X },
};

const MailRow = ({ m, onReview }) => {
  const [open, setOpen] = useState(false);
  const meta = STATUS_META[m.status] || STATUS_META.received;
  const Icon = meta.icon;
  const canReview = ['held_for_review', 'matched_needs_owner', 'quarantined'].includes(m.status);
  return (
    <div className="glass-card border border-white/10 p-4" data-testid={`inbound-mail-${m.id}`}>
      <button className="w-full text-left" onClick={() => setOpen(!open)} data-testid={`inbound-mail-toggle-${m.id}`}>
        <div className="flex items-center justify-between gap-3 flex-wrap">
          <div className="min-w-0">
            <p className="text-sm text-white font-medium truncate">{m.subject || '(no subject)'}</p>
            <p className="text-xs text-muted-foreground font-mono truncate">{m.from_addr} · {m.received_at ? new Date(m.received_at).toLocaleString() : ''}</p>
          </div>
          <span className={`text-[10px] font-bold px-2 py-1 rounded border flex items-center gap-1 ${meta.cls}`}>
            <Icon className="w-3 h-3" /> {meta.label}
          </span>
        </div>
      </button>
      {open && (
        <div className="mt-3 pt-3 border-t border-white/10 space-y-2 text-sm">
          {m.quarantine_reason && (
            <p className="text-red-400 text-xs font-mono">Quarantine reason: {m.quarantine_reason}</p>
          )}
          {m.match && (
            <p className="text-xs text-blue-300 font-mono">
              Matched: {m.match.vendor} → {m.match.project_title}
            </p>
          )}
          {m.extracted && (
            <div className="text-xs text-zinc-300 font-mono space-y-0.5">
              {m.extracted.summary && <p>Summary: {m.extracted.summary}</p>}
              {m.extracted.unit_price != null && <p>Extracted unit price: ${Number(m.extracted.unit_price).toLocaleString()} {m.extracted.currency || 'USD'}</p>}
              {m.extracted.moq && <p>MOQ: {m.extracted.moq}</p>}
              {m.extracted.lead_time_days != null && <p>Lead time: {m.extracted.lead_time_days} days</p>}
            </div>
          )}
          {m.text && <p className="text-xs text-zinc-400 whitespace-pre-wrap max-h-48 overflow-y-auto">{m.text.slice(0, 2000)}</p>}
          {(m.attachments || []).length > 0 && (
            <p className="text-xs text-muted-foreground">Attachments: {m.attachments.map(a => a.filename).join(', ')}</p>
          )}
          {canReview && (
            <div className="flex gap-2 pt-1">
              <button onClick={() => onReview(m.id, 'reviewed')} data-testid={`inbound-mark-reviewed-${m.id}`}
                className="text-xs px-3 py-1.5 rounded border border-green-500/40 text-green-400 hover:bg-green-500/10 transition-colors">
                Mark reviewed
              </button>
              <button onClick={() => onReview(m.id, 'dismissed')} data-testid={`inbound-dismiss-${m.id}`}
                className="text-xs px-3 py-1.5 rounded border border-white/20 text-zinc-400 hover:bg-white/5 transition-colors">
                Dismiss
              </button>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default function LeeMail() {
  const [emails, setEmails] = useState([]);
  const [status, setStatus] = useState(null);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    try {
      const [listRes, stRes] = await Promise.all([
        axios.get(`${API}/inbound/emails`, { headers: getAuthHeaders() }),
        axios.get(`${API}/inbound/status`, { headers: getAuthHeaders() }),
      ]);
      setEmails(listRes.data.emails || []);
      setStatus(stRes.data);
    } catch (e) {
      toast.error('Failed to load inbound mail');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const onReview = async (id, action) => {
    try {
      await axios.post(`${API}/inbound/emails/${id}/review`, { action }, { headers: getAuthHeaders() });
      toast.success(`Marked ${action}`);
      load();
    } catch (e) {
      toast.error('Review failed');
    }
  };

  const counts = status?.counts || {};
  return (
    <Layout>
      <div className="space-y-6 max-w-4xl" data-testid="lee-mail-page">
        <div>
          <h1 className="text-3xl font-bold text-white flex items-center gap-3">
            <Inbox className="w-7 h-7 text-primary" /> Inbound Mail
          </h1>
          <p className="text-sm text-muted-foreground mt-1">
            Mail to quotes@ and lee@leelife.us routes here 24/7 once deployed. Vendor replies
            auto-match to their quote threads; everything else waits for you.
          </p>
        </div>

        <div className="flex flex-wrap gap-3" data-testid="inbound-counts">
          {['quote_applied', 'matched_needs_owner', 'held_for_review', 'quarantined'].map(s => (
            <div key={s} className={`px-3 py-2 rounded border text-xs font-mono ${STATUS_META[s].cls}`}>
              {STATUS_META[s].label}: {counts[s] || 0}
            </div>
          ))}
        </div>

        <div className="glass-card border border-white/10 p-4 text-xs text-muted-foreground space-y-1" data-testid="inbound-security-policy">
          <p className="font-bold uppercase tracking-widest text-zinc-400">Security policy (enforced in code)</p>
          {(status?.security_policy || []).map((p, i) => <p key={i}>• {p}</p>)}
        </div>

        {loading ? (
          <p className="text-muted-foreground text-sm">Loading…</p>
        ) : emails.length === 0 ? (
          <div className="glass-card border border-white/10 p-8 text-center text-muted-foreground text-sm" data-testid="inbound-empty">
            No inbound mail yet. After deploy + MX setup, vendor replies land here automatically.
          </div>
        ) : (
          <div className="space-y-3">
            {emails.map(m => <MailRow key={m.id} m={m} onReview={onReview} />)}
          </div>
        )}
      </div>
    </Layout>
  );
}
